import { useSyncExternalStore } from 'react';

/**
 * Opening a chat marks it read on the phone (sendSeen) and clears the local
 * unread badge. Debug users can turn that off so browsing the inbox leaves the
 * customer's unread state untouched.
 *
 * Storage: `bt-team-inbox-send-seen` — absent means ON (the default), only the
 * literal string 'false' disables it. The toggle itself is exposed only when
 * `bt-debug` is 'true'; the preference still applies if it was left off.
 */
export const SEND_SEEN_STORAGE_KEY = 'bt-team-inbox-send-seen';

const SEND_SEEN_CHANGE_EVENT = 'bt-team-inbox-send-seen-change';

const readStorage = (key: string): string | null => {
    try {
        return typeof localStorage === 'undefined' ? null : localStorage.getItem(key);
    } catch {
        return null;
    }
};

export const isSendSeenEnabled = (): boolean => readStorage(SEND_SEEN_STORAGE_KEY) !== 'false';

export const isSendSeenToggleVisible = (): boolean => readStorage('bt-debug') === 'true';

export const setSendSeenEnabled = (enabled: boolean): void => {
    try {
        localStorage.setItem(SEND_SEEN_STORAGE_KEY, enabled ? 'true' : 'false');
    } catch { /* private mode — the in-memory subscribers still update */ }
    window.dispatchEvent(new CustomEvent(SEND_SEEN_CHANGE_EVENT));
};

const subscribe = (onChange: () => void) => {
    // Local writes fire the custom event; `storage` covers other tabs.
    window.addEventListener(SEND_SEEN_CHANGE_EVENT, onChange);
    window.addEventListener('storage', onChange);
    return () => {
        window.removeEventListener(SEND_SEEN_CHANGE_EVENT, onChange);
        window.removeEventListener('storage', onChange);
    };
};

export const useSendSeenEnabled = (): boolean =>
    useSyncExternalStore(subscribe, isSendSeenEnabled, () => true);
